// Derived (computed) form-field values — transform pipeline engine.
//
// Mirrored by backend/services/field_transforms.py — the two share a test
// table (fieldTransforms.test.js / field_transforms_test.py); change them in
// lockstep.
//
// A derived field is declared by config.derive on a field:
//
//   "config": {
//     "derive": {
//       "source_field_id": "<uuid>",
//       "pipeline": [{ "op": "url_domain_root" }, { "op": "title_case" }],
//       "on_empty": "omit",          // omit | source | default
//       "default_value": ""
//     }
//   }
//
// THE SERVER IS AUTHORITATIVE. routes/forms.py recomputes every derived value
// at submit and overwrites whatever the client sent. This module exists so the
// live form can (a) route client-side branches on a derived value and (b) let
// a show_when condition reference one. Regex steps in particular may differ
// between the JS and Python flavours — the builder's preview calls the server
// endpoint (POST /api/forms/transform/preview) rather than this module.

const MAX_INPUT_CHARS = 4096;
const MAX_PIPELINE_STEPS = 20;

// Two-part public suffixes. Mirrors MULTI_PART_TLDS in field_transforms.py.
const MULTI_PART_TLDS = new Set([
    'co.uk', 'org.uk', 'ac.uk', 'gov.uk', 'me.uk',
    'co.nz', 'co.za', 'co.jp', 'co.kr', 'co.in', 'co.id', 'co.th', 'co.il',
    'com.au', 'net.au', 'org.au', 'com.br', 'com.sg', 'com.ph', 'com.my',
    'com.mx', 'com.tr', 'com.cn', 'com.hk', 'com.tw', 'com.ar', 'com.co',
    'com.pe', 'com.ua', 'com.vn', 'com.pk', 'com.eg', 'com.sa',
]);

// Mirror of field_transforms._to_str. Two deliberate choices, both for parity:
//
//   * Empty list elements are skipped, so [null, 'x'] joins to 'x' rather than
//     ', x'. A stray separator would otherwise land in a CRM field.
//   * Plain objects are not a valid answer shape (answers arrive as string,
//     list, bool, or number from JSON). They coerce to '' rather than to
//     '[object Object]' here / "{'a': 1}" in Python — a silent divergence.
const toStr = (v) => {
    if (v === true) return 'yes';
    if (v === false) return 'no';
    if (v === null || v === undefined) return '';
    if (Array.isArray(v)) return v.map(toStr).filter((p) => p !== '').join(', ');
    if (typeof v === 'object') return '';
    return String(v);
};

// Python's str.title() uppercases the first letter of each letter-run and
// lowercases the rest; digits are not letters, so they break a run.
// \p{L} keeps that behaviour unicode-aware, matching Python's isalpha().
const titleCase = (v) =>
    v.replace(/\p{L}+/gu, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// ── url helpers ─────────────────────────────────────────────────────────────

function urlHost(value) {
    const v = String(value || '').trim();
    if (!v) return '';
    // A bare host has no scheme; URL() needs one. Use a throwaway scheme and
    // read back the host so port and credentials are stripped for us.
    const withScheme = v.includes('://') ? v : `http://${v}`;
    try {
        return new URL(withScheme).hostname.toLowerCase();
    } catch {
        return '';
    }
}

function urlDomainRoot(value) {
    let host = urlHost(value);
    if (!host) return '';
    // Reject anything that is not plausibly a host. Without this, free text
    // falls straight through: "not a url" -> title_case -> "Not A Url" lands
    // in GoHighLevel as a company name, and on_empty cannot catch it because
    // the result is not empty.
    if (host.includes(' ') || !host.includes('.')) return '';
    if (host.startsWith('www.')) host = host.slice(4);
    const labels = host.split('.').filter(Boolean);
    if (labels.length <= 1) return labels[0] || '';
    if (labels.length >= 3 && MULTI_PART_TLDS.has(labels.slice(-2).join('.'))) {
        return labels[labels.length - 3];
    }
    return labels[labels.length - 2];
}

function urlParts(value) {
    const v = String(value || '').trim();
    const withScheme = v.includes('://') ? v : `http://${v}`;
    try {
        return new URL(withScheme);
    } catch {
        return null;
    }
}

// ── step handlers ───────────────────────────────────────────────────────────
// Every handler: (value: string, step: object, answers: object) => string
// Unknown ops are a no-op (forward compatibility with a newer builder).
// Throwing is caught by runPipeline and downgraded to a no-op.

const STEPS = {
    // text
    trim: (v) => v.trim(),
    collapse_spaces: (v) => v.split(/\s+/).filter(Boolean).join(' '),
    lowercase: (v) => v.toLowerCase(),
    uppercase: (v) => v.toUpperCase(),
    title_case: (v) => titleCase(v),
    sentence_case: (v) => (v ? v[0].toUpperCase() + v.slice(1).toLowerCase() : v),
    prefix: (v, s) => `${s.text ?? ''}${v}`,
    suffix: (v, s) => `${v}${s.text ?? ''}`,
    replace: (v, s) => {
        const find = s.find ?? '';
        const withText = s.with ?? '';
        if (!find) return v;
        const all = s.all !== false;
        const flags = `${all ? 'g' : ''}${s.case_sensitive ? '' : 'i'}`;
        return v.replace(new RegExp(escapeRe(find), flags), withText);
    },
    slice: (v, s) => {
        const start = Number.isFinite(Number(s.start)) ? Number(s.start) : 0;
        const end = s.end === null || s.end === undefined ? undefined : Number(s.end);
        return v.slice(start, end);
    },
    slugify: (v, s) => {
        const sep = s.separator ?? '-';
        return v
            .split('')
            .map((c) => (/[\p{L}\p{N}]/u.test(c) ? c.toLowerCase() : ' '))
            .join('')
            .split(/\s+/)
            .filter(Boolean)
            .join(sep);
    },
    keep_only: (v, s) => {
        const cs = s.charset || 'alphanumeric';
        if (cs === 'digits') return v.replace(/[^\p{Nd}]/gu, '');
        if (cs === 'letters') return v.replace(/[^\p{L}]/gu, '');
        return v.replace(/[^\p{L}\p{N}]/gu, '');
    },
    remove_chars: (v, s) => {
        const chars = new Set((s.chars || '').split(''));
        return v.split('').filter((c) => !chars.has(c)).join('');
    },
    // split
    split_take: (v, s) => {
        const sep = s.separator ?? '.';
        const parts = sep ? v.split(sep) : [v];
        const idx = Number.isFinite(Number(s.index)) ? Number(s.index) : 0;
        const norm = idx < 0 ? parts.length + idx : idx;
        return norm >= 0 && norm < parts.length ? parts[norm] : '';
    },
    before_first: (v, s) => v.split(s.separator ?? '.')[0],
    after_first: (v, s) => {
        const sep = s.separator ?? '.';
        const i = v.indexOf(sep);
        return i === -1 ? '' : v.slice(i + sep.length);
    },
    before_last: (v, s) => {
        const sep = s.separator ?? '.';
        const i = v.lastIndexOf(sep);
        return i === -1 ? v : v.slice(0, i);
    },
    after_last: (v, s) => {
        const sep = s.separator ?? '.';
        const i = v.lastIndexOf(sep);
        return i === -1 ? v : v.slice(i + sep.length);
    },
    // url
    url_host: (v) => urlHost(v),
    strip_www: (v) => (v.toLowerCase().startsWith('www.') ? v.slice(4) : v),
    url_domain_root: (v) => urlDomainRoot(v),
    strip_tld: (v) => (v.includes('.') ? v.slice(0, v.lastIndexOf('.')) : v),
    url_path: (v) => urlParts(v)?.pathname ?? '',
    url_query_param: (v, s) => urlParts(v)?.searchParams.get(s.name || '') ?? '',
    // email
    email_domain: (v) => (v.includes('@') ? v.slice(v.lastIndexOf('@') + 1) : ''),
    email_local: (v) => (v.includes('@') ? v.slice(0, v.indexOf('@')) : v),
    // number
    digits_only: (v) => v.replace(/[^\p{Nd}]/gu, ''),
    to_number: (v, s) => {
        const n = Number(v);
        if (v === '' || Number.isNaN(n)) return '';
        return s.decimals === null || s.decimals === undefined
            ? n
            : Number(n.toFixed(Number(s.decimals)));
    },
    // compose
    append_field: (v, s, answers) => {
        const other = toStr(answers?.[s.field_id]);
        if (!other) return v;
        return v ? `${v}${s.separator ?? ' '}${other}` : other;
    },
    // regex (escape hatch) — server is authoritative; see the header comment
    regex_extract: (v, s) => {
        const rx = new RegExp(s.pattern || '', s.ignore_case ? 'i' : '');
        const m = rx.exec(v.slice(0, MAX_INPUT_CHARS));
        if (!m) return '';
        const g = s.group === undefined ? 1 : Number(s.group);
        return m[g] !== undefined ? m[g] : (m[0] || '');
    },
    regex_replace: (v, s) => {
        const flags = `${s.all !== false ? 'g' : ''}${s.ignore_case ? 'i' : ''}`;
        const rx = new RegExp(s.pattern || '', flags);
        return v.slice(0, MAX_INPUT_CHARS).replace(rx, s.replacement ?? '');
    },
    // fallback
    default_if_empty: (v, s) => (v ? v : (s.value ?? '')),
};

/** Run an ordered step list. Never throws — a failing step is a no-op. */
export function runPipeline(raw, pipeline, answers = {}) {
    let value = toStr(raw).slice(0, MAX_INPUT_CHARS);
    for (const step of (pipeline || []).slice(0, MAX_PIPELINE_STEPS)) {
        if (!step || typeof step !== 'object') continue;
        const fn = STEPS[step.op];
        if (!fn) continue; // unknown op — forward compatibility
        try {
            value = fn(value, step, answers);
        } catch {
            /* failing step is a no-op, matching the Python engine */
        }
    }
    return value;
}

const deriveCfg = (field) => {
    const cfg = field?.config?.derive;
    return cfg && typeof cfg === 'object' && cfg.source_field_id ? cfg : null;
};

/**
 * Return ONLY the derived key/values for the given answer set. Callers merge
 * the result over `answers`. Idempotent. A cycle resolves to empty (fail
 * closed) — contrast fieldConditions, which fails OPEN.
 */
export function applyDerivedFields(fields, answers) {
    const derived = (fields || []).filter((f) => f?.field_id && deriveCfg(f));
    if (!derived.length) return {}; // fast path — existing forms

    const byId = {};
    derived.forEach((f) => { byId[f.field_id] = f; });
    const out = {};
    const resolving = [];
    const cycleNodes = new Set();

    const resolve = (field) => {
        const fid = field.field_id;
        if (fid in out) return out[fid];
        if (resolving.includes(fid)) {
            // Poison EVERY member of the current chain, not just the node where
            // the cycle was detected. Otherwise the result depends on which
            // field happens to be resolved first: one member would consume the
            // other's on_empty fallback as a real value. Poisoning the whole
            // chain makes each member fall back to its OWN on_empty policy,
            // which is deterministic and order-independent.
            resolving.slice(resolving.indexOf(fid)).forEach((n) => cycleNodes.add(n));
            return '';
        }
        resolving.push(fid);
        try {
            const cfg = deriveCfg(field);
            const srcId = cfg.source_field_id;
            const raw = (byId[srcId] && srcId !== fid)
                ? resolve(byId[srcId])              // chained derive
                : answers?.[srcId];
            let value = runPipeline(raw, cfg.pipeline || [], { ...answers, ...out });
            if (cycleNodes.has(fid)) value = '';    // fail closed for cycle members
            if (value === '' || value === null || value === undefined) {
                const mode = cfg.on_empty || 'omit';
                if (mode === 'source') value = toStr(raw);
                else if (mode === 'default') value = cfg.default_value ?? '';
                else { delete out[fid]; return ''; }  // omit — key stays absent
            }
            out[fid] = value;
            return value;
        } finally {
            resolving.pop();
        }
    };

    derived.forEach(resolve);
    return out;
}

/**
 * Visibility mode is DERIVED, never stored — which is why no migration is
 * needed. Existing forms classify themselves correctly on first render.
 */
export function visibilityMode(field) {
    if (field?.hidden) return 'hidden';
    if (field?.show_when) return 'conditional';
    return 'always';
}

/** Field types that may carry a derive config (single-value text-ish targets). */
export const DERIVABLE_FIELD_TYPES = ['text', 'textarea', 'email', 'phone', 'url', 'number'];

/**
 * Drives the Add-step picker in DeriveEditor. `params` describes each step's
 * editable arguments; `advanced: true` renders the warning treatment.
 */
export const STEP_CATALOGUE = [
    // URL — listed first: the canonical use case is website → company name
    { op: 'url_domain_root', group: 'URL', label: 'Extract domain root', hint: 'https://www.multiplai.tech/x → multiplai', params: [] },
    { op: 'url_host', group: 'URL', label: 'Host only', hint: 'https://www.x.tech/a → www.x.tech', params: [] },
    { op: 'strip_www', group: 'URL', label: 'Strip www.', hint: 'www.x.tech → x.tech', params: [] },
    { op: 'strip_tld', group: 'URL', label: 'Strip TLD', hint: 'x.tech → x', params: [] },
    { op: 'url_path', group: 'URL', label: 'Path only', hint: 'https://x.tech/careers → /careers', params: [] },
    { op: 'url_query_param', group: 'URL', label: 'Query parameter', hint: '?ref=abc → abc', params: [{ key: 'name', type: 'string', label: 'Parameter name', required: true }] },
    // Text
    { op: 'trim', group: 'Text', label: 'Trim whitespace', params: [] },
    { op: 'collapse_spaces', group: 'Text', label: 'Collapse spaces', params: [] },
    { op: 'title_case', group: 'Text', label: 'Title Case', hint: 'multiplai → Multiplai', params: [] },
    { op: 'sentence_case', group: 'Text', label: 'Sentence case', params: [] },
    { op: 'lowercase', group: 'Text', label: 'lowercase', params: [] },
    { op: 'uppercase', group: 'Text', label: 'UPPERCASE', params: [] },
    { op: 'prefix', group: 'Text', label: 'Add prefix', params: [{ key: 'text', type: 'string', label: 'Text' }] },
    { op: 'suffix', group: 'Text', label: 'Add suffix', params: [{ key: 'text', type: 'string', label: 'Text' }] },
    { op: 'replace', group: 'Text', label: 'Find & replace', params: [
        { key: 'find', type: 'string', label: 'Find', required: true },
        { key: 'with', type: 'string', label: 'Replace with' },
        { key: 'case_sensitive', type: 'boolean', label: 'Match case', default: false },
    ] },
    { op: 'slice', group: 'Text', label: 'Take characters', params: [
        { key: 'start', type: 'number', label: 'From', default: 0 },
        { key: 'end', type: 'number', label: 'To (blank = end)' },
    ] },
    { op: 'slugify', group: 'Text', label: 'Slugify', hint: 'Acme Corp → acme-corp', params: [{ key: 'separator', type: 'string', label: 'Separator', default: '-' }] },
    { op: 'keep_only', group: 'Text', label: 'Keep only…', params: [{ key: 'charset', type: 'select', label: 'Characters', options: ['alphanumeric', 'letters', 'digits'], default: 'alphanumeric' }] },
    { op: 'remove_chars', group: 'Text', label: 'Remove characters', params: [{ key: 'chars', type: 'string', label: 'Characters to remove' }] },
    // Split
    { op: 'split_take', group: 'Split', label: 'Split & take part', hint: 'index -1 = last', params: [
        { key: 'separator', type: 'string', label: 'Separator', default: '.' },
        { key: 'index', type: 'number', label: 'Part index', default: 0 },
    ] },
    { op: 'before_first', group: 'Split', label: 'Before first…', params: [{ key: 'separator', type: 'string', label: 'Separator', default: '.' }] },
    { op: 'after_first', group: 'Split', label: 'After first…', params: [{ key: 'separator', type: 'string', label: 'Separator', default: '.' }] },
    { op: 'before_last', group: 'Split', label: 'Before last…', params: [{ key: 'separator', type: 'string', label: 'Separator', default: '.' }] },
    { op: 'after_last', group: 'Split', label: 'After last…', params: [{ key: 'separator', type: 'string', label: 'Separator', default: '.' }] },
    // Email
    { op: 'email_domain', group: 'Email', label: 'Email domain', hint: 'a@x.tech → x.tech', params: [] },
    { op: 'email_local', group: 'Email', label: 'Email name', hint: 'a@x.tech → a', params: [] },
    // Number
    { op: 'digits_only', group: 'Number', label: 'Digits only', hint: '+63 917 → 63917', params: [] },
    { op: 'to_number', group: 'Number', label: 'Convert to number', hint: 'must be the last step', params: [{ key: 'decimals', type: 'number', label: 'Decimal places (blank = as-is)' }] },
    // Advanced
    { op: 'append_field', group: 'Advanced', label: 'Append another field', params: [
        { key: 'field_id', type: 'field', label: 'Field', required: true },
        { key: 'separator', type: 'string', label: 'Separator', default: ' ' },
    ] },
    { op: 'regex_extract', group: 'Advanced', label: 'Regex extract', advanced: true, params: [
        { key: 'pattern', type: 'string', label: 'Pattern', required: true, mono: true },
        { key: 'group', type: 'number', label: 'Capture group', default: 1 },
        { key: 'ignore_case', type: 'boolean', label: 'Ignore case', default: false },
    ] },
    { op: 'regex_replace', group: 'Advanced', label: 'Regex replace', advanced: true, params: [
        { key: 'pattern', type: 'string', label: 'Pattern', required: true, mono: true },
        { key: 'replacement', type: 'string', label: 'Replace with' },
        { key: 'ignore_case', type: 'boolean', label: 'Ignore case', default: false },
    ] },
    // Fallback
    { op: 'default_if_empty', group: 'Fallback', label: 'Default if empty', params: [{ key: 'value', type: 'string', label: 'Fallback value' }] },
];

export const STEP_BY_OP = Object.fromEntries(STEP_CATALOGUE.map((s) => [s.op, s]));
export const STEP_GROUPS = [...new Set(STEP_CATALOGUE.map((s) => s.group))];
